import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import type { WebSearchResultsPart } from './types';

const WebSearchResults: React.FC<{ data: WebSearchResultsPart }> = ({ data }) => {
  const { colors } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  if (data.search_status === 'blocked') {
    return (
      <div style={{
        margin: '12px 0',
        backgroundColor: colors.surface,
        borderRadius: '12px',
        overflow: 'hidden',
        border: `1px solid ${colors.border}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
      }}>
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            padding: '12px 16px',
            borderBottom: isExpanded ? `1px solid ${colors.borderLight}` : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: colors.surfaceElevated,
            cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>⚠️</span>
            <span style={{ fontWeight: 600, fontSize: '14px', color: colors.textPrimary }}>
              Web Search Blocked
            </span>
          </div>
          <span style={{
            fontSize: '12px',
            color: colors.textMuted,
            transition: 'transform 0.2s ease',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
          }}>
            ▶
          </span>
        </div>

        {isExpanded && (
          <div style={{ padding: '12px 16px' }}>
            <div style={{ fontSize: '13px', color: colors.textPrimary, lineHeight: '1.4', marginBottom: '8px' }}>
              The search request was blocked because it may have contained sensitive information
              such as personally identifiable information (PII) or restricted content.
            </div>
            <div style={{ fontSize: '12px', color: colors.textMuted }}>
              Try rephrasing your search without using personal or sensitive information.
            </div>
          </div>
        )}
      </div>
    );
  }

  if (data.results.length === 0) {
    return null;
  }

  return (
    <div style={{
      margin: '12px 0',
      backgroundColor: colors.surface,
      borderRadius: '12px',
      overflow: 'hidden',
      border: `1px solid ${colors.border}`,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    }}>
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          padding: '12px 16px',
          borderBottom: isExpanded ? `1px solid ${colors.borderLight}` : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: colors.surfaceElevated,
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>🔎</span>
          <span style={{ fontWeight: 600, fontSize: '14px', color: colors.textPrimary }}>
            Web Search Results
          </span>
          <span style={{
            fontSize: '12px',
            color: colors.textMuted,
            marginLeft: '6px',
            backgroundColor: colors.surface,
            padding: '2px 6px',
            borderRadius: '12px'
          }}>
            {data.results.length} {data.results.length === 1 ? 'result' : 'results'}
          </span>
          {data.search_status === 'redacted' && (
            <span style={{
              fontSize: '11px',
              color: '#e67e22',
              backgroundColor: ('#e67e22') + '20',
              padding: '2px 8px',
              borderRadius: '12px',
              fontWeight: 500,
            }}>
              query modified
            </span>
          )}
        </div>

        <span style={{
          fontSize: '12px',
          color: colors.textMuted,
          transition: 'transform 0.2s ease',
          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
        }}>
          ▶
        </span>
      </div>

      {isExpanded && (
        <div>
          {data.search_status === 'redacted' && data.original_query && (
            <div style={{
              padding: '10px 16px',
              backgroundColor: colors.surfaceElevated,
              borderBottom: `1px solid ${colors.borderLight}`,
              fontSize: '13px',
              color: colors.textPrimary,
              lineHeight: '1.5',
            }}>
              <div style={{ marginBottom: '4px', fontSize: '12px', color: colors.textMuted }}>
                Your search query was modified to remove sensitive information.
              </div>
              <div><strong>Original:</strong> {data.original_query}</div>
              {data.modified_query && (
                <div style={{ marginTop: '2px' }}><strong>Modified:</strong> {data.modified_query}</div>
              )}
            </div>
          )}

          {data.results.map((result, index) => {
            let hostname = '';
            try { hostname = new URL(result.url).hostname.replace(/^www\./, ''); } catch { /* ignore */ }

            return (
              <div key={index} style={{
                padding: '12px 16px',
                borderBottom: index < data.results.length - 1 ? `1px solid ${colors.borderLight}` : 'none',
              }}
              className="search-result-item"
              >
                <a
                  href={result.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: colors.primary,
                    textDecoration: 'none',
                    fontWeight: 500,
                    fontSize: '14px',
                    display: 'block',
                    marginBottom: '6px'
                  }}
                >
                  {result.title}
                </a>
                <div style={{
                  fontSize: '13px',
                  color: colors.textPrimary,
                  lineHeight: '1.4',
                  margin: '4px 0'
                }}>
                  {result.content}
                </div>
                <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '4px' }}>
                  {hostname}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        .search-result-item:hover {
          background-color: ${colors.buttonHover};
        }
        .search-result-item a:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default WebSearchResults;